import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Smartphone, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface MFAStatus {
  enabled: boolean;
  method?: string;
  backupCodesRemaining?: number;
}

function MFA() {
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBackupCodesOpen, setIsBackupCodesOpen] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/mfa/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMfaStatus(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableMFA = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/mfa/enable`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchMFAStatus();
      }
    } catch (error) {
      console.error('Failed to enable MFA:', error);
    }
  };

  const handleDisableMFA = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/mfa/disable`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchMFAStatus();
      }
    } catch (error) {
      console.error('Failed to disable MFA:', error);
    }
  };

  const handleGenerateBackupCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users/me/mfa/backup-codes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.data?.codes || []);
        setIsBackupCodesOpen(true);
        fetchMFAStatus();
      }
    } catch (error) {
      console.error('Failed to generate backup codes:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-8 w-8" />
            Multi-Factor Authentication
          </h2>
          <p className="text-muted-foreground mt-2">
            Enhance your account security with MFA
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>MFA Status</CardTitle>
          <CardDescription>Current multi-factor authentication settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mfaStatus ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Status</p>
                  <p className="text-sm text-muted-foreground">
                    MFA is {mfaStatus.enabled ? 'enabled' : 'disabled'}
                  </p>
                </div>
                <Badge className={mfaStatus.enabled ? 'bg-green-500' : 'bg-gray-500'}>
                  {mfaStatus.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              {mfaStatus.enabled && (
                <>
                  {mfaStatus.method && (
                    <div>
                      <p className="font-medium">Method</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Smartphone className="h-4 w-4" />
                        {mfaStatus.method}
                      </p>
                    </div>
                  )}

                  {mfaStatus.backupCodesRemaining !== undefined && (
                    <div>
                      <p className="font-medium">Backup Codes</p>
                      <p className="text-sm text-muted-foreground">
                        {mfaStatus.backupCodesRemaining} backup codes remaining
                      </p>
                    </div>
                  )}
                </>
              )}

              <div className="flex gap-4">
                {mfaStatus.enabled ? (
                  <>
                    <Button variant="outline" onClick={handleDisableMFA}>
                      Disable MFA
                    </Button>
                    <Button variant="outline" onClick={handleGenerateBackupCodes}>Generate New Backup Codes</Button>
                  </>
                ) : (
                  <Button onClick={handleEnableMFA}>Enable MFA</Button>
                )}
              </div>
            </>
          ) : (
            <p className="text-center text-muted-foreground">Unable to load MFA status</p>
          )}
        </CardContent>
      </Card>

      {!mfaStatus?.enabled && (
        <Card>
          <CardHeader>
            <CardTitle>How MFA Works</CardTitle>
            <CardDescription>Protect your account with an extra layer of security</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Multi-factor authentication adds an extra layer of security to your account by requiring
              more than just a password to sign in. When enabled, you'll need to provide both your
              password and a verification code from your mobile device.
            </p>
            <div className="space-y-2">
              <h4 className="font-medium">Benefits:</h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Prevents unauthorized access even if your password is compromised</li>
                <li>Protects sensitive data and operations</li>
                <li>Industry-standard security practice</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backup Codes Dialog */}
      <Dialog open={isBackupCodesOpen} onOpenChange={setIsBackupCodesOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Backup Codes</DialogTitle>
            <DialogDescription>
              Save these backup codes in a secure location
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                <p className="font-semibold mb-1">Important:</p>
                <p>Your old backup codes are now invalid. Save these new codes in a secure location. You will not be able to see them again.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <div
                  key={index}
                  className="p-2 bg-muted rounded font-mono text-sm text-center"
                >
                  {code}
                </div>
              ))}
            </div>
            {backupCodes.length === 0 && (
              <p className="text-center text-muted-foreground text-sm">
                No backup codes generated
              </p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setIsBackupCodesOpen(false)}>
              I've Saved These Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MFA;
